/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomExportButton.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 07-02-2025
 * @description : Custom export button for importing from JSON or Excel.
 */

import { Box, Menu, MenuItem } from "@mui/material";
import * as XLSX from "xlsx";
import React, { useState } from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CustomButtonV2 from "../customComponentsV2/common/button/CustomButtonV2";

/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 07-02-2025
 * @description : Custom import button for importing from JSON or Excel.
 * @param {}
 * @return : The rendered export button
 */

function CustomExportButton({ fileName, exportDataExcel, exportDataJson }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handles exporting data as an Excel file with multiple sheets.
  const handleExportExcel = () => {
    handleClose();

    if (!exportDataExcel || Object.keys(exportDataExcel).length === 0) return;

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Loop through object keys and create sheets dynamically
    Object.entries(exportDataExcel).forEach(([sheetName, sheetData]) => {
      if (Array.isArray(sheetData) && sheetData.length > 0) {
        const worksheet = XLSX.utils.json_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(wb, worksheet, sheetName);
      }
    });

    // Generate and download Excel file
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };

  // Handles exporting data as a JSON file.
  const handleExportJson = () => {
    handleClose();
    if (!exportDataJson || Object.keys(exportDataJson).length === 0) return;

    const jsonData = JSON.stringify(exportDataJson, null, 2); // Pretty-print JSON
    const blob = new Blob([jsonData], { type: "application/json" });
    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = `${fileName}.json`; // Save file with ".json" extension
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>
      <CustomButtonV2
        size={"small"}
        btnName={"Export"}
        startIcon={<FileDownloadIcon />}
        handleOnClick={(e) => setAnchorEl(e.currentTarget)}
        btnStyle={{
          ml: 2,
        }}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleExportJson()}>Export JSON</MenuItem>
        <MenuItem onClick={() => handleExportExcel()}>Export Excel</MenuItem>
      </Menu>
    </Box>
  );
}

export default CustomExportButton;
